import { useQueries } from '@tanstack/react-query';
import { Panel, PanelHeader, Stamp } from '@/components/ui';
import { checkGap, gapsFor } from '@/pages/gaps';

/**
 * What the workflow still needs from the backend, checked against the backend.
 *
 * Written down, this list would be wrong the morning after someone fixes an
 * entry. So each gap carries the request that would settle it and the page
 * asks: what is shown is what the API answers now. A gap that closes turns
 * green here without anyone editing this file.
 *
 * It is deliberately in the product rather than in a document, because the
 * person who needs to know what cannot be done today is the person operating
 * it, and the person who needs the list is the engineer who can close it.
 */
export function GapReport({ bucketId, companyId }: { bucketId: string | null; companyId: string | null }) {
  const gaps = gapsFor(bucketId, companyId);
  const results = useQueries({
    queries: gaps.map((gap) => ({
      queryKey: ['gap', gap.id, gap.probe],
      queryFn: () => checkGap(gap),
      staleTime: 60_000,
      retry: false,
    })),
  });

  const settled = results.filter((r) => r.data);
  const open = settled.filter((r) => r.data!.state === 'open').length;
  const closed = settled.filter((r) => r.data!.state === 'closed').length;

  return (
    <Panel>
      <PanelHeader
        title="What the workflow still needs"
        sub={
          settled.length < gaps.length
            ? 'asking the backend about each one'
            : `${open} still open, ${closed} closed · each one re-checked against the API just now`
        }
      />
      {gaps.map((gap, i) => {
        const r = results[i]?.data;
        const state = r?.state ?? 'checking';
        return (
          <div key={gap.id} className="border-b border-line px-4 py-3 last:border-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-row text-ink">{gap.title}</span>
              <Stamp
                tone={state === 'closed' ? 'ok' : state === 'open' ? 'warn' : 'mut'}
                className="ml-auto"
              >
                {state === 'closed' ? 'DONE' : state === 'open' ? 'OPEN' : state === 'unknown' ? 'UNCLEAR' : 'CHECKING'}
              </Stamp>
            </div>
            <p className="mt-1 text-small text-ink-2">{gap.costs}</p>
            {state !== 'closed' && <p className="mt-1 text-caption text-ink-3">Needs: {gap.needs}</p>}
            {r?.evidence && <p className="mt-1 text-caption text-ink-4">{r.evidence}</p>}
            <p className="mono mt-1 text-caption text-ink-4">
              GET {gap.probe}
              {r?.status ? ` · answered ${r.status}` : ''}
            </p>
          </div>
        );
      })}
      <p className="border-t border-line px-4 py-2.5 text-caption text-ink-3">
        Every check here is a read. Nothing on this page starts a run, writes a record, or costs anything.
      </p>
    </Panel>
  );
}
